import { useMemo } from "react"
import { Form, FormFeedback, Label, Button, Input } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"

import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"

const ImporterBankAccountAddEdit = ({
  modal,
  toggleModal,
  isEdit = false,
  editedData = null,
  onSubmit,
  loading = false,
}) => {
  const validationSchema = useMemo(
    () =>
      Yup.object({
        label: Yup.string().nullable(),
        account_name: Yup.string().trim().required("Please enter account name"),
        account_number: Yup.string().trim().required("Please enter account number"),
        routing_number: Yup.string().nullable(),
        account_mobile_number: Yup.string()
          .trim()
          .required("Please enter account mobile number")
          .max(20, "Account mobile number cannot exceed 20 characters"),
        bank_name: Yup.string().trim().required("Please enter bank name"),
        branch_name: Yup.string().nullable(),
        is_default: Yup.boolean(),
        is_active: Yup.boolean(),
      }),
    []
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      label: editedData?.label || "",
      account_name: editedData?.account_name || "",
      account_number: editedData?.account_number || "",
      routing_number: editedData?.routing_number || "",
      account_mobile_number: editedData?.account_mobile_number || "",
      bank_name: editedData?.bank_name || "",
      branch_name: editedData?.branch_name || "",
      is_default: editedData?.is_default ?? false,
      is_active: editedData?.is_active ?? true,
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: values => onSubmit?.(values),
  })

  const renderField = (key, label, type = "text", opts = {}) => (
    <div className="mb-3">
      <Label htmlFor={key}>{label}</Label>
      <InputField
        type={type}
        name={key}
        id={key}
        value={formik.values[key]}
        onChange={formik.handleChange}
        invalid={formik.touched[key] && !!formik.errors[key]}
        {...opts}
      />
      {formik.touched[key] && formik.errors[key] ? (
        <FormFeedback type="invalid">{formik.errors[key]}</FormFeedback>
      ) : null}
    </div>
  )

  const buttonText = `${isEdit ? "Save Changes" : "Add Bank Account"}${
    loading ? "..." : ""
  }`

  return (
    <ThemeModal
      isOpen={modal}
      toggle={toggleModal}
      title={isEdit ? "Edit Bank Account" : "Add Bank Account"}
    >
      <Form
        onSubmit={e => {
          e.preventDefault()
          formik.handleSubmit()
        }}
      >
        {renderField("label", "Label (optional)")}
        {renderField("account_name", "Account Name")}
        {renderField("account_number", "Account Number")}
        {renderField("routing_number", "Routing Number (optional)")}
        {renderField("account_mobile_number", "Account Mobile Number")}
        {renderField("bank_name", "Bank Name")}
        {renderField("branch_name", "Branch Name (optional)")}

        <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
          <Button
            type="button"
            color={formik.values.is_default ? "success" : "light"}
            outline={!formik.values.is_default}
            size="sm"
            onClick={() =>
              formik.setFieldValue("is_default", !formik.values.is_default)
            }
          >
            <i
              className={`bx ${
                formik.values.is_default ? "bx-check-double" : "bx-circle"
              } me-1`}
            />
            {formik.values.is_default ? "Default Account" : "Mark as Default"}
          </Button>

          <Button
            type="button"
            color={formik.values.is_active ? "success" : "secondary"}
            outline={!formik.values.is_active}
            size="sm"
            onClick={() =>
              formik.setFieldValue("is_active", !formik.values.is_active)
            }
          >
            <i
              className={`bx ${
                formik.values.is_active ? "bx-check-circle" : "bx-x-circle"
              } me-1`}
            />
            {formik.values.is_active ? "Active" : "Inactive"}
          </Button>
        </div>

        <div className="text-end">
          <Button color="secondary" outline onClick={toggleModal} className="me-2">
            Cancel
          </Button>
          <Button color="success" type="submit" disabled={loading}>
            {buttonText}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default ImporterBankAccountAddEdit
